import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  betaGetGmailImapSettings,
  betaUpdateGmailImapSettings,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ImapSettingsModel } from '../../../../../types';
import { Controller, useForm } from 'react-hook-form';

import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export default function ImapSettingsPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const {
    data: imapSettings,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.ImapSettings, userEmail],
    queryFn: () => betaGetGmailImapSettings(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('imap settings'),
    },
  });
  interface FormInput {
    enabled: boolean;
    autoExpunge: boolean;
    expungeBehavior: string;
    maxFolderSize: number;
  }

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    formState: { isDirty },
  } = useForm<FormInput>({
    values: {
      enabled: imapSettings?.enabled ? imapSettings.enabled : false,
      autoExpunge: imapSettings?.autoExpunge ? imapSettings.autoExpunge : false,
      expungeBehavior: imapSettings?.expungeBehavior
        ? imapSettings.expungeBehavior
        : 'archive',
      maxFolderSize: imapSettings?.maxFolderSize
        ? imapSettings.maxFolderSize
        : 0,
    },
  });
  const onSubmit = (data: FormInput) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.EditGmailImapSettings.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const { mutate, isPending: isUpdating } = useMutation({
    mutationFn: (data: ImapSettingsModel) =>
      betaUpdateGmailImapSettings(userEmail, data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('IMAP settings'),
      });
      queryClient.setQueryData([UserKeys.ImapSettings, userEmail], data);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('IMAP settings'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .update_imap_settings,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.imapSettings,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const expungeBehaviorOptions = [
    { value: 'archive', displayValue: 'Archive' },
    { value: 'deleteForever', displayValue: 'Delete Forever' },
    { value: 'trash', displayValue: 'Trash' },
  ];

  const maxFolderSizeOptions = [
    { value: 0, displayValue: 'No Limit' },
    { value: 1000, displayValue: 1000 },
    { value: 2000, displayValue: 2000 },
    { value: 5000, displayValue: 5000 },
    { value: 10000, displayValue: 10000 },
  ];

  if (isLoading || isFetching) {
    return (
      <Card
        sx={{
          height: '100%',
        }}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', minHeight: '100%' }}
        >
          <CircularProgress />
        </Stack>
      </Card>
    );
  }

  const autoExpungedEnabled = watch('autoExpunge');
  const imapEnabled = watch('enabled');

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        action={
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={
              !isDirty ||
              !isAbleToSeeUIElement(
                userRoles,
                UserRoles.EditGmailImapSettings.value
              )
            }
            color="primary"
            type="submit"
          >
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Save'
            )}
          </Button>
        }
      />

      <CardContent
        sx={{
          px: 4,
          py: 0,
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <Stack gap={3}>
          <Stack flexDirection="row" alignItems="center" gap={6}>
            <Typography>Imap Enabled: </Typography>
            <Stack
              flexDirection="row"
              component="label"
              alignItems="center"
              justifyContent="center"
            >
              <Typography>Disabled</Typography>
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => (
                  <Switch
                    onChange={() => field.onChange(!field.value)}
                    checked={field.value}
                  />
                )}
              />
              <Typography>Enabled</Typography>
            </Stack>
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap={6}>
            <Typography>Auto Expunge: </Typography>
            <Stack
              flexDirection="row"
              component="label"
              alignItems="center"
              justifyContent="center"
            >
              <Typography>Disabled</Typography>
              <Controller
                name="autoExpunge"
                control={control}
                render={({ field }) => (
                  <Switch
                    disabled={!imapEnabled}
                    onChange={() => {
                      field.onChange(!field.value);
                      if (!field.value) {
                        setValue('expungeBehavior', 'archive');
                      }
                    }}
                    checked={field.value}
                  />
                )}
              />
              <Typography>Enabled</Typography>
            </Stack>
          </Stack>
          <Stack flexDirection={'row'} gap={2} alignItems="center">
            <Typography>Expunge Behavior:</Typography>
            <Controller
              name={'expungeBehavior'}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  {...field}
                  disabled={autoExpungedEnabled || !imapEnabled}
                  sx={{ width: 300 }}
                >
                  {expungeBehaviorOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Stack flexDirection={'row'} gap={4} alignItems="center">
            <Typography>Max Folder Size:</Typography>
            <Controller
              name={'maxFolderSize'}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  {...field}
                  disabled={!imapEnabled}
                  sx={{ width: 300 }}
                >
                  {maxFolderSizeOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() =>
              mutate({
                enabled: getValues('enabled'),
                autoExpunge: getValues('autoExpunge'),
                expungeBehavior: getValues('expungeBehavior'),
                maxFolderSize: getValues('maxFolderSize'),
              })
            }
            mutateLoading={isUpdating}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
